import { useState, type ReactNode, type ComponentType } from 'react'
import { Minus, Plus } from 'lucide-react'
import { cn } from 'components/UI/cn'

export interface TreeNode {
  /** Unique identifier for the node */
  id: string
  /** Primary label — supports ReactNode for Links, styled text, etc. */
  label: ReactNode
  /** Secondary text below label (smaller, muted) */
  subtitle?: ReactNode
  /** Lucide icon component displayed next to the label */
  icon?: ComponentType<{ size?: number; className?: string }>
  /** Child nodes — presence enables the +/- expand toggle */
  children?: TreeNode[]
  /** Whether this node starts expanded (default: true) */
  defaultExpanded?: boolean
}

interface TreeListProps {
  /** Root-level tree nodes */
  nodes: TreeNode[]
  /** Additional className for the root container */
  className?: string
}

/**
 * Lightweight recursive tree view for sidebar hierarchies.
 *
 * Renders nodes with expand/collapse toggles, entity icons, and
 * vertical connector lines between siblings. Labels accept ReactNode
 * so callers can pass Links, buttons, or styled text.
 */
export function TreeList({ nodes, className }: TreeListProps) {
  return (
    <div className={cn('flex flex-col gap-1 text-sm', className)}>
      {nodes.map((node) => (
        <TreeNodeRow key={node.id} node={node} depth={0} />
      ))}
    </div>
  )
}

function TreeNodeRow({ node, depth }: { node: TreeNode; depth: number }) {
  const [expanded, setExpanded] = useState(node.defaultExpanded ?? true)
  const hasChildren = node.children && node.children.length > 0
  const Icon = node.icon

  return (
    <div>
      <div className="flex items-start gap-1.5">
        {/* +/- toggle or spacer */}
        {hasChildren ? (
          <button
            type="button"
            onClick={() => setExpanded(!expanded)}
            className="flex-shrink-0 p-0.5 -ml-0.5 rounded hover:bg-surface-hover transition-colors"
          >
            {expanded ? (
              <Minus size={14} className="text-fg-secondary" />
            ) : (
              <Plus size={14} className="text-fg-secondary" />
            )}
          </button>
        ) : (
          <span className="w-[18px] flex-shrink-0" />
        )}

        {/* Entity icon */}
        {Icon && (
          <Icon size={14} className="text-fg-secondary mt-0.5 flex-shrink-0" />
        )}

        {/* Label + subtitle */}
        <div className="min-w-0 flex-1">
          <div className="leading-snug">{node.label}</div>
          {node.subtitle && (
            <p className="text-xs text-fg-secondary truncate">
              {node.subtitle}
            </p>
          )}
        </div>
      </div>

      {/* Children — connector line only on outermost level */}
      {expanded && hasChildren && (
        <div
          className={cn(
            'flex flex-col gap-1 mt-1',
            depth === 0 ? 'ml-[8px] pl-3 border-l border-line' : 'ml-1'
          )}
        >
          {node.children!.map((child) => (
            <TreeNodeRow key={child.id} node={child} depth={depth + 1} />
          ))}
        </div>
      )}
    </div>
  )
}
